import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { api } from "@/lib/api";
import { getActiveLeagueId, setActiveLeagueId, clearActiveLeague, getDeviceUserId, clearDeviceUserId } from "@/lib/league";
import { LeagueProvider } from "@/lib/LeagueContext";
import SwitchProfile from "@/pages/SwitchProfile";
import SetupScreen from "@/pages/SetupScreen";
import PersonalDashboard from "@/pages/PersonalDashboard";
import WelcomeScreen from "@/pages/WelcomeScreen";

function Gatekeeper() {
  const [loading, setLoading] = useState(true);
  const [league, setLeague] = useState(null);
  const [autoRoute, setAutoRoute] = useState(null);

  useEffect(() => {
    (async () => {
      const id = getActiveLeagueId();
      const deviceUserId = getDeviceUserId();

      // SECURITY (P0): a brand-new device MUST hit /welcome. Never auto-pull
      // the global most-recent league. Both an explicit active-league id AND a
      // verified device-user id from THIS league are required to bypass /welcome.
      if (!id) {
        clearDeviceUserId(); // nuke any orphan device token from prior versions
        setLoading(false);
        return;
      }

      try {
        const l = await api.getLeagueById(id);
        if (!l) {
          clearActiveLeague();
          clearDeviceUserId();
          setLoading(false);
          return;
        }
        setLeague(l);

        // Remember-Me bypass: only if device token belongs to THIS league's user
        if (deviceUserId) {
          try {
            const u = await api.getUser(deviceUserId);
            if (u && u.league_id === l.id) {
              setAutoRoute(`/dashboard/${deviceUserId}`);
            } else {
              clearDeviceUserId();
            }
          } catch {
            clearDeviceUserId();
          }
        }
      } catch {
        clearActiveLeague();
        clearDeviceUserId();
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--stakes-bg)]">
        <div className="label-mono">LOADING STAKES…</div>
      </div>
    );
  }
  if (autoRoute) return <Navigate to={autoRoute} replace />;
  if (!league) return <Navigate to="/welcome" replace />;
  return <SwitchProfile league={league} />;
}

function App() {
  return (
    <div className="App grain">
      <BrowserRouter>
        <LeagueProvider>
          <Routes>
            <Route path="/" element={<Gatekeeper />} />
            <Route path="/welcome" element={<WelcomeScreen />} />
            <Route path="/setup" element={<SetupScreen />} />
            <Route path="/dashboard/:userId" element={<PersonalDashboard />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </LeagueProvider>
      </BrowserRouter>
      <Toaster theme="dark" position="top-center" />
    </div>
  );
}

export default App;
